import React, { useRef, useState } from 'react';
import { message } from 'antd';
import { Dayjs } from 'dayjs';
import type { IDatasource, GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import AppGrid from '../../components/AppGrid';
import ContentLayout from '../../components/ContentLayout';
import Header from '../../components/Header/Header';
import DashboardHeaderActions from '../../components/HeaderActions/DashboardHeaderActions';
import { columnDefs } from '../../components/constants/grid';
import InspectionInfoDrawer from '../../features/inspections/components/InspectionInfoDrawer';
import type { InspectionsListResponse, VinInspectionItem } from '../../features/inspections';
import { getInspectionSelection } from '../../features/inspections/inspectionUtils';
import { inspectionApi } from '../../features/inspections/inspectionAPI';
import { store } from '../../store/store';
import { DateRangeFilter } from '../../types/common/dashboard';
import { toApiDateTime, toApiDayStart, toApiDayEnd } from '../../utils/datetime';

const formatFilterObject = (filterModel: any = {}) => {
  const params: Record<string, any> = {};

  for (const [key, value] of Object.entries<any>(filterModel)) {
    if (!value?.filterType) continue;

    // AG Grid's agDateColumnFilter emits a timezone-less local wall-clock date
    // (e.g. "2026-07-07 00:00:00"). Convert the selected local day(s) into
    // offset-qualified ISO boundaries so the API filters by the user's local day
    // rather than a UTC day.
    if (value.filterType === 'date') {
      if (value.dateFrom === undefined && value.dateTo === undefined) continue;
      params.fromDate = toApiDayStart(value.dateFrom ?? value.dateTo);
      params.toDate = toApiDayEnd(value.dateTo ?? value.dateFrom);
      continue;
    }

    if (value.filter === undefined) continue;
    if (key === 'damageCount') {
      const filter = value.filter;

      // Legacy boolean filter from the old Damages / No damages checkboxes.
      if (typeof filter === 'boolean') {
        if (filter === false) {
          params.damageSources = 'noDamages';
        }
        continue;
      }

      if (filter && typeof filter === 'object') {
        const sources = Array.isArray(filter.sources) ? [...filter.sources] : [];

        // Legacy object shape that used a separate noDamages / hasDamages flag.
        if ((filter.noDamages || filter.hasDamages === false) && !sources.includes('noDamages')) {
          sources.push('noDamages');
        }

        if (sources.length > 0) {
          params.damageSources = sources.join(',');
        }
      }
      continue;
    }

    params[key] = value.filter;
  }

  return params;
};

const formatSortParams = (sortModel: any[] = []) => {
  if (!sortModel.length) return {};

  const { colId, sort } = sortModel[0];
  return {
    sortBy: colId,
    sortOrder: sort,
  };
};

const SIZE = 100;

const Dashboard = () => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedInspection, setSelectedInspection] = useState<VinInspectionItem | null>(null);
  const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeFilter>({
    startDate: null,
    endDate: null,
  });
  const [isApplyDisabled, setIsApplyDisabled] = useState(true);

  const onGridReady = (params: GridReadyEvent) => {
    gridRef.current?.api.sizeColumnsToFit();
    serverUpdate(params);
  };

  const downloadJSONAsExcel = async (blobData: Blob) => {
    try {
      const url = window.URL.createObjectURL(blobData);
      const a = document.createElement('a');
      a.href = url;
      a.download = `VIN_Inspections_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download file');
    }
  };

  const serverUpdate = (gridParams: any) => {
    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: async params => {
        try {
          setLoading(true);
          const api = gridRef.current?.api;
          api?.hideOverlay();

          const { filterModel, sortModel, startRow } = params;
          console.log('filterModel', filterModel);
          const start = startRow + 1;
          const page = Math.floor(start / SIZE);

          const queryParams = {
            page: page + 1,
            pageSize: SIZE,
            ...formatFilterObject(filterModel),
            ...formatSortParams(sortModel),
          };

          setFilters(queryParams);
          const result = await store.dispatch(
            inspectionApi.endpoints.getInspections.initiate(queryParams, {
              subscribe: false,
            })
          );

          if (!('data' in result)) {
            throw result.error;
          }

          const { items = [], meta } = result.data as InspectionsListResponse;
          if (!items.length) {
            api?.showNoRowsOverlay();
          } else {
            api?.hideOverlay();
          }

          params.successCallback(items, meta.totalItems);
          setLoading(false);
        } catch (error) {
          console.error('Grid API error', error);
          params.failCallback();
          setLoading(false);
        }
      },
    };

    gridParams.api.setGridOption('datasource', dataSource);
  };

  const onRangeChange = (dates: (Dayjs | null)[] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRangeFilter({ startDate: null, endDate: null });
      setIsApplyDisabled(true);
      return;
    }
    setIsApplyDisabled(false);
    const [start, end] = dates;
    setDateRangeFilter({
      startDate: start,
      endDate: end,
    });
  };

  const onChange = (e: any) => {
    setSearchTerm(e.target.value);
    setIsApplyDisabled(e.target.value === '');
  };

  const onSearch = (value: any) => {
    const api = gridRef.current?.api;
    if (!api) return;

    const filterModel: any = {};

    if (value) {
      filterModel.search = {
        filterType: 'text',
        type: 'contains',
        filter: value,
      };
    }
    api.setFilterModel(filterModel);
  };

  const onApply = () => {
    const api = gridRef.current?.api;
    if (!api) return;

    const filterModel: any = {};

    if (searchTerm) {
      filterModel.search = {
        filterType: 'text',
        type: 'contains',
        filter: searchTerm,
      };
      api.setFilterModel(filterModel);
    }

    if (dateRangeFilter?.startDate && dateRangeFilter?.endDate) {
      // Send the exact picked instants with the local timezone offset (the toolbar
      // RangePicker has showTime), so the API stores/queries an unambiguous instant.
      filterModel.fromDate = {
        filterType: 'text',
        type: 'contains',
        filter: toApiDateTime(dateRangeFilter.startDate),
      };
      filterModel.toDate = {
        filterType: 'text',
        type: 'contains',
        filter: toApiDateTime(dateRangeFilter.endDate),
      };
    }
    api.setFilterModel(filterModel);
    setIsApplyDisabled(true);
  };

  const onClear = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    setSearchTerm('');
    setDateRangeFilter({
      startDate: null,
      endDate: null,
    });
  };

  const onExport = async () => {
    const result = await store.dispatch(inspectionApi.endpoints.exportInspections.initiate(filters));

    if ('data' in result && result.data) {
      downloadJSONAsExcel(result.data);
    }
  };

  const onCellClicked = (params: any) => {
    const row = params.data as VinInspectionItem;

    if (params.column.getColId() === 'info') {
      setSelectedInspection(row);
      setIsInfoDrawerOpen(true);
      return;
    }

    const { vin } = row;
    const { inspectionId, inspectionDateTime } = getInspectionSelection(row);
    const searchParams = new URLSearchParams();

    if (inspectionId) {
      searchParams.set('inspectionId', inspectionId);
    }
    if (inspectionDateTime) {
      searchParams.set('inspectionDateTime', inspectionDateTime);
    }

    const query = searchParams.toString();
    const url = query ? `/vin/${vin}?${query}` : `/vin/${vin}`;
    const win = window.open(url, '_blank');
    if (win != null) {
      win.focus();
    }
  };

  return (
    <>
      <Header
        title="Vins"
        headerActions={
          <DashboardHeaderActions
            onRangeChange={onRangeChange}
            onChange={onChange}
            onSearch={onSearch}
            onApply={onApply}
            onClear={onClear}
            onExport={onExport}
            searchTerm={searchTerm}
            dateRangeFilter={dateRangeFilter}
            isApplyDisabled={isApplyDisabled}
          />
        }
      />
      <ContentLayout>
        <AppGrid
          gridRef={gridRef}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
          onCellClicked={onCellClicked}
          loading={loading}
        />
      </ContentLayout>
      <InspectionInfoDrawer inspection={selectedInspection} open={isInfoDrawerOpen} onClose={() => setIsInfoDrawerOpen(false)} />
    </>
  );
};

export default Dashboard;
